import React, { useRef, useState } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import { Link } from "react-router-dom";
import HeroSection from "../../components/HeroSection";
import { FaArrowRight, FaHeart, FaCheckCircle, FaHandshake, FaUsers } from "react-icons/fa";
import healthOutreach1 from "../../assets/gallery/health/health-outreach1.jpeg";
import donor from "../../assets/gallery/health/donor.jpeg";
import { donorRequestAPI } from "../../services/api";
import toast from "react-hot-toast";
import { validateForm as validateFormUtil } from "../../utils/validation";

const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  if (isInView) {
    controls.start("visible");
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function BeADonor() {
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const rules = {
      full_name: { required: true, label: 'Full Name' },
      email: { required: true, type: 'email', label: 'Email' },
      phone: { required: true, type: 'phone', label: 'Phone Number' },
    };
    const newErrors = validateFormUtil(formData, rules);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    setIsSubmitting(true);

    try {
      await donorRequestAPI.create({
        request_type: 'individual_donor',
        ...formData
      });
      toast.success('Your request has been submitted successfully! Our team will reach out to you soon.');
      setFormData({
        full_name: "",
        email: "",
        phone: "",
        message: ""
      });
      setShowRequestForm(false);
    } catch (error) {
      toast.error(error.message || 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const donationOptions = [
    {
      icon: <FaHeart className="text-3xl text-primary-600" />,
      title: "One-Time Donation",
      description: "Make a single contribution to support our programs and initiatives.",
      amount: "Any amount"
    },
    {
      icon: <FaHandshake className="text-3xl text-primary-600" />,
      title: "Monthly Giving",
      description: "Become a monthly donor and provide consistent support for our ongoing programs.",
      amount: "$25 - $500+"
    },
    {
      icon: <FaUsers className="text-3xl text-primary-600" />,
      title: "Scholarship Fund",
      description: "Contribute specifically to our scholarship program, helping students achieve their educational goals.",
      amount: "Any amount"
    }
  ];

  const impactAreas = [
    "Scholarship programs for students",
    "Health outreach and medical missions",
    "Community development projects",
    "Educational and training programs",
    "Cultural preservation initiatives",
    "Mentorship and career development"
  ];

  return (
    <div className="w-full bg-white">
      <HeroSection
        title="Be A Donor"
        subtitle="Your generous support helps us create lasting impact in our community and hometown. Every contribution makes a difference."
        buttons={[
          { text: "Apply Now", link: "/register", variant: "blue" },
          { text: "Login", link: "/login", variant: "blue" }
        ]}
        heroImage={donor}
      />

      {/* Introduction */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  fontWeight: 700,
                  color: '#1F2937'
                }}
              >
                Make a Difference Today
              </h2>
              <div
                className="mx-auto mb-6"
                style={{
                  width: '80px',
                  height: '3px',
                  backgroundColor: '#F59E0B'
                }}
              ></div>
              <p
                className="text-lg leading-relaxed"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  fontWeight: 400,
                  color: '#374151',
                  lineHeight: 1.8
                }}
              >
                Your donation directly supports our programs and initiatives, helping us create positive change
                in our community and hometown. We ensure transparency in how donations are used and provide regular
                updates on the impact of your contributions.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Donation Options */}
      <section className="section bg-gray-50">
        <div className="section-container">
          <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {donationOptions.map((option, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div
                  className="card text-center"
                  style={{
                    minHeight: '280px'
                  }}
                >
                  <div className="flex justify-center mb-6">
                    {option.icon}
                  </div>
                  <h3
                    className="text-xl font-bold mb-3"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1F2937',
                      lineHeight: 1.3
                    }}
                  >
                    {option.title}
                  </h3>
                  <p
                    className="text-primary-600 font-semibold mb-4"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 600,
                      color: '#1E40AF'
                    }}
                  >
                    {option.amount}
                  </p>
                  <p
                    className="text-gray-600 text-sm flex-grow"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 400,
                      color: '#374151',
                      lineHeight: 1.6
                    }}
                  >
                    {option.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Where Your Donation Goes
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We ensure transparency in how donations are used. Your contribution supports various
                programs and initiatives that directly benefit our community.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {impactAreas.map((area, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div className="card">
                  <div className="flex items-start gap-3">
                    <FaCheckCircle className="text-primary-600 text-xl flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{area}</span>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Request Form Section */}
      {showRequestForm && (
        <section className="section bg-white">
          <div className="section-container">
            <AnimatedSection>
              <div className="max-w-2xl mx-auto">
                <div className="card">
                  <h3
                    className="text-2xl font-bold mb-6"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1F2937'
                    }}
                  >
                    Request to Be a Donor
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-600 focus:border-primary-600 ${errors.full_name ? 'border-red-500' : 'border-gray-300'
                          }`}
                      />
                      {errors.full_name && <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-600 focus:border-primary-600 ${errors.email ? 'border-red-500' : 'border-gray-300'
                          }`}
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-600 focus:border-primary-600 ${errors.phone ? 'border-red-500' : 'border-gray-300'
                          }`}
                      />
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message (Optional)
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-primary-600"
                        placeholder="Tell us about your interest in becoming a donor..."
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 font-semibold"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Request'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowRequestForm(false)}
                        className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 font-semibold"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section
        className="section text-center"
        style={{
          backgroundColor: '#F9FAFB'
        }}
      >
        <div className="section-container">
          <AnimatedSection>
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 700,
                color: '#1F2937'
              }}
            >
              Ready to Make a Difference?
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto mb-8"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 400,
                color: '#374151',
                lineHeight: 1.6
              }}
            >
              Submit a request to become a donor and our team will reach out to you to discuss donation options.
            </p>
            <button
              onClick={() => setShowRequestForm(true)}
              className="inline-flex items-center justify-center gap-2 px-8 py-3 font-semibold rounded-md transition-colors"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 600,
                backgroundColor: '#1E40AF',
                color: '#FFFFFF',
                textDecoration: 'none',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1E3A8A';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1E40AF';
              }}
            >
              Request to Be a Donor
              <FaArrowRight />
            </button>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
